import moment from "moment/moment";
import React from "react";
import { Link } from "react-router-dom";
import {
  FiActivity,
  FiArrowRight,
  FiClock,
  FiDownload,
  FiFileText,
  FiMessageCircle,
  FiPrinter,
  FiSend,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import { MdOutlineAccountTree, MdOutlineTipsAndUpdates } from "react-icons/md";
import Layout from "../../components/layouts/Layout";
import {
  exportConversionsMonthlyOverviewCsv,
  fetchAutomationFlows,
  fetchConversionsMonthlyOverview,
  fetchSmsConversations,
} from "./AutomationTemplates/automationTemplateApi";
import SmsAccessGuard from "./Components/SmsAccessGuard";
import { isActiveAutomationFlow } from "./AutomationTemplates/automationFlowStatus";
import { readAutomationFlowReplyCount } from "./AutomationTemplates/automationFlowMetrics";
import "./style/sms-billing.css";

const getErrorMessage = (error, fallback) => {
  const responseMessage = error && error.response && error.response.data && error.response.data.message;
  const integrationMessage = error && error.integrationMessage;

  if (typeof responseMessage === "string" && responseMessage.trim()) return responseMessage;
  if (typeof integrationMessage === "string" && integrationMessage.trim()) return integrationMessage;

  return fallback;
};

const invalidResponseError = (payload) => {
  const error = new Error("Invalid conversations dashboard response");
  error.integrationMessage = payload && payload.message;
  return error;
};

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const readFirstDefined = (source, keys) => {
  if (!source) return null;
  const key = keys.find((item) => source[item] !== undefined && source[item] !== null);
  return key ? source[key] : null;
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.data)) return value.data;
  return [];
};

const normaliseCollection = (payload, keys) => {
  const source = (payload && payload.data) || payload || {};
  const collection = keys.map((key) => source[key]).find(Boolean) || source.data || (Array.isArray(source) ? source : []);
  const data = asArray(collection);

  return {
    data,
    total: Number(collection.total || source.total || data.length || 0),
  };
};

const normaliseConversation = (conversation, index) => {
  const customer = readFirstDefined(conversation, ["customer", "contact", "guest", "lead"]) || {};
  const unreadCount = Number(readFirstDefined(conversation, ["unread_count", "unreadCount"]) || 0);
  const name = readFirstDefined(conversation, ["customer_name", "customerName", "contact_name", "name"])
    || readFirstDefined(customer, ["name", "full_name", "fullName"])
    || "Customer";
  const phone = readFirstDefined(conversation, ["customer_phone", "phone", "phone_number", "mobile"])
    || readFirstDefined(customer, ["phone", "phone_number", "mobile"])
    || "";
  const status = String(readFirstDefined(conversation, ["status", "state"]) || (unreadCount ? "awaiting_reply" : "open")).toLowerCase();

  return {
    id: readFirstDefined(conversation, ["id", "conversation_id", "conversationId", "uuid"]) || phone || index,
    name,
    phone,
    preview: readFirstDefined(conversation, [
      "last_message",
      "lastMessage",
      "last_message_preview",
      "lastMessagePreview",
      "preview",
    ]) || "No message preview available.",
    time: readFirstDefined(conversation, [
      "last_activity_at",
      "lastActivityAt",
      "last_message_at",
      "lastMessageAt",
      "updated_at",
      "updatedAt",
    ]),
    status: unreadCount > 0 ? "awaiting_reply" : status,
  };
};

const normaliseFlow = (flow, index) => {
  const event = readFirstDefined(flow, ["event", "event_details", "eventDetails"]) || {};
  return {
    id: readFirstDefined(flow, ["id", "flow_id", "flowId", "automation_flow_id"]) || index,
    name: readFirstDefined(flow, ["name", "flow_name", "flowName", "title"]) || "Untitled automation",
    eventName: readFirstDefined(flow, ["event_name", "eventName"])
      || readFirstDefined(event, ["name", "event_name", "title"])
      || "No event assigned",
    replies: Number(readAutomationFlowReplyCount(flow) || 0),
    updatedAt: readFirstDefined(flow, ["updated_at", "updatedAt", "last_updated"]),
  };
};

const formatNumber = (value) => {
  if (!hasValue(value) || Number.isNaN(Number(value))) return "Not available";
  return Number(value).toLocaleString();
};

const formatMoney = (value, currency) => {
  if (!hasValue(value) || Number.isNaN(Number(value))) return "Not available";
  const amount = Number(value);
  const currencyCode = currency || "AUD";

  try {
    return new Intl.NumberFormat("en-AU", { style: "currency", currency: currencyCode }).format(amount);
  } catch (error) {
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
};

const formatShortDate = (value) => {
  if (!value) return "—";
  const date = moment(value);
  if (!date.isValid()) return "—";
  return date.isSame(moment(), "day") ? date.format("h:mm A") : date.format("MMM D");
};

const getInitials = (name) => String(name || "Customer")
  .split(" ")
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part.charAt(0).toUpperCase())
  .join("");

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

class SmsBilling extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedMonth: "",
      overview: null,
      loading: false,
      exportingCsv: false,
      error: "",
      conversations: [],
      conversationsLoading: false,
      conversationsError: "",
      awaitingReplies: 0,
      awaitingLoading: false,
      awaitingError: "",
      activeFlows: [],
      activeFlowTotal: 0,
      flowsLoading: false,
      flowsError: "",
    };
    this.isMountedFlag = false;
    this.requestId = 0;
  }

  componentDidMount() {
    this.isMountedFlag = true;
    this.loadOverview();
    this.loadRecentConversations();
    this.loadAwaitingReplies();
    this.loadActiveFlows();
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
  }

  loadOverview = async (month) => {
    const requestId = ++this.requestId;
    const params = month ? { month } : undefined;
    this.setState({ loading: true, error: "" });

    try {
      const response = await fetchConversionsMonthlyOverview(params);
      const payload = response && response.data;
      const overview = payload && payload.data;
      if (!overview || !overview.billing_month || payload.success === false) throw invalidResponseError(payload);

      if (this.isMountedFlag && requestId === this.requestId) {
        this.setState({
          selectedMonth: overview.billing_month.value || month || "",
          overview,
          loading: false,
          error: "",
        });
      }
    } catch (error) {
      if (this.isMountedFlag && requestId === this.requestId) {
        this.setState((state) => ({
          selectedMonth: state.overview && state.overview.billing_month
            ? state.overview.billing_month.value
            : state.selectedMonth,
          loading: false,
          error: getErrorMessage(error, "Monthly conversations data is unavailable right now."),
        }));
      }
    }
  };

  loadRecentConversations = async () => {
    this.setState({ conversationsLoading: true, conversationsError: "" });
    try {
      const response = await fetchSmsConversations({ page: 1, per_page: 3 });
      const collection = normaliseCollection(response && response.data, ["conversations", "sms_conversations", "smsConversations"]);
      if (this.isMountedFlag) {
        this.setState({ conversations: collection.data.slice(0, 3).map(normaliseConversation), conversationsLoading: false });
      }
    } catch (error) {
      if (this.isMountedFlag) {
        this.setState({ conversationsLoading: false, conversationsError: "Recent conversations could not be loaded." });
      }
    }
  };

  loadAwaitingReplies = async () => {
    this.setState({ awaitingLoading: true, awaitingError: "" });
    try {
      const response = await fetchSmsConversations({ page: 1, per_page: 1, status: "awaiting_reply" });
      const collection = normaliseCollection(response && response.data, ["conversations", "sms_conversations", "smsConversations"]);
      if (this.isMountedFlag) this.setState({ awaitingReplies: collection.total, awaitingLoading: false });
    } catch (error) {
      if (this.isMountedFlag) this.setState({ awaitingLoading: false, awaitingError: "Waiting replies are unavailable." });
    }
  };

  loadActiveFlows = async () => {
    this.setState({ flowsLoading: true, flowsError: "" });
    try {
      const response = await fetchAutomationFlows({ page: 1, per_page: 1000 });
      const collection = normaliseCollection(response && response.data, ["flows", "automation_flows", "automationFlows"]);
      const activeFlows = collection.data.filter(isActiveAutomationFlow);
      if (this.isMountedFlag) {
        this.setState({
          activeFlows: activeFlows.slice(0, 3).map(normaliseFlow),
          activeFlowTotal: activeFlows.length,
          flowsLoading: false,
        });
      }
    } catch (error) {
      if (this.isMountedFlag) this.setState({ flowsLoading: false, flowsError: "Active automation flows could not be loaded." });
    }
  };

  handleMonthChange = (event) => {
    const selectedMonth = event.target.value;
    this.setState({ selectedMonth }, () => this.loadOverview(selectedMonth));
  };

  handleExportCsv = async () => {
    const { overview, exportingCsv } = this.state;
    const month = overview && overview.billing_month && overview.billing_month.value;
    if (!month || exportingCsv) return;

    this.setState({ exportingCsv: true, error: "" });
    try {
      const response = await exportConversionsMonthlyOverviewCsv({ month });
      downloadBlob(response.data, `conversations-billing-${month}.csv`);
    } catch (error) {
      if (this.isMountedFlag) this.setState({ error: "The billing CSV could not be downloaded. Please try again." });
    } finally {
      if (this.isMountedFlag) this.setState({ exportingCsv: false });
    }
  };

  handleExportPdf = () => {
    if (this.state.overview) window.print();
  };

  renderInitialState() {
    const { loading, error } = this.state;
    if (loading) {
      return (
        <div className="conversations-initial-state">
          <span className="spinner-border" role="status" aria-label="Loading conversations dashboard" />
          <p>Loading your conversations dashboard…</p>
        </div>
      );
    }
    return (
      <div className="conversations-error-state" role="alert">
        <span>{error || "Monthly conversations data is unavailable right now."}</span>
        <button type="button" onClick={() => this.loadOverview()}>Retry</button>
      </div>
    );
  }

  renderHero() {
    return (
      <section className="conversations-hero conversion-no-print">
        <div className="conversations-hero-copy">
          <h1>The Venue Revenue Loop</h1>
          <p>Automated 2 way SMS conversations that turn guest replies into bookings, memberships, function enquiries and repeat visits.</p>
          <div className="conversations-hero-actions">
            <Link to="/conversions/sms-automation-templates" className="conversations-primary-button">
              <FiZap /> Create Automation Flow
            </Link>
            <Link to="/conversions/sms-automation-templates" className="conversations-secondary-button">
              <FiFileText /> Browse Templates
            </Link>
          </div>
        </div>
        <div className="conversations-hero-art" aria-hidden="true">
          <div className="hero-spark spark-one">✦</div>
          <div className="hero-spark spark-two">✦</div>
          <div className="hero-message-bubble"><span /><span /><span /></div>
          <div className="hero-phone">
            <div className="hero-phone-speaker" />
            <div className="hero-phone-line short" />
            <div className="hero-phone-line" />
            <div className="hero-phone-line medium" />
            <div className="hero-phone-reply" />
          </div>
          <FiSend className="hero-send-icon" />
        </div>
      </section>
    );
  }

  renderTabs() {
    const tabs = [
      ["Overview", "/conversions", true],
      ["Conversations", "/conversions/conversations"],
      ["Notifications", "/conversions/notifications"],
      ["Analytics", "/conversions/analytics"],
      ["My Automation Flows", "/conversions/automation-flows"],
      ["Browse Templates", "/conversions/sms-automation-templates"],
      ["Billing", "/conversions/billing"],
    ];
    return (
      <nav className="conversations-tabs conversion-no-print" aria-label="Conversations sections">
        {tabs.map(([label, path, active]) => (
          <Link key={path} to={path} className={active ? "active" : ""}>{label}</Link>
        ))}
      </nav>
    );
  }

  renderMetricCards() {
    const { overview, activeFlowTotal, awaitingReplies, awaitingLoading, awaitingError, flowsLoading, flowsError } = this.state;
    const totals = overview.overview || {};
    const cards = [
      { label: "Active Automation Flows", value: flowsError ? "—" : flowsLoading ? "…" : formatNumber(activeFlowTotal), icon: <MdOutlineAccountTree />, tone: "gold" },
      { label: "Replies Waiting", value: awaitingError ? "—" : awaitingLoading ? "…" : formatNumber(awaitingReplies), icon: <FiMessageCircle />, tone: "blue" },
      { label: "Replies Received", value: formatNumber(totals.replies_received), icon: <FiUsers />, tone: "green" },
      { label: "SMS Sent This Month", value: formatNumber(totals.sms_sent), icon: <FiSend />, tone: "violet" },
    ];
    return (
      <section className="conversations-metrics">
        {cards.map((card) => (
          <article className="conversation-metric-card" key={card.label}>
            <div className={`conversation-metric-icon ${card.tone}`}>{card.icon}</div>
            <div><span>{card.label}</span><strong>{card.value}</strong></div>
          </article>
        ))}
      </section>
    );
  }

  renderGettingStarted() {
    const steps = [
      { title: "Choose a template", text: "Pick a template that fits your event.", icon: <FiFileText /> },
      { title: "Set your message", text: "Customise your message and timing.", icon: <FiMessageCircle /> },
      { title: "Activate & track", text: "Launch your flow and watch replies come in.", icon: <FiSend /> },
    ];
    return (
      <section className="conversation-panel getting-started-panel conversion-no-print">
        <div className="conversation-panel-header"><h2>Get started in 3 simple steps</h2></div>
        <div className="getting-started-steps">
          {steps.map((step, index) => (
            <div className="getting-started-step" key={step.title}>
              <span className="step-number">{index + 1}</span>
              <div className="step-icon">{step.icon}</div>
              <strong>{step.title}</strong>
              <p>{step.text}</p>
            </div>
          ))}
        </div>
        <Link to="/conversions/sms-automation-templates" className="conversations-primary-button compact">Browse Templates</Link>
      </section>
    );
  }

  renderRecentConversations() {
    const { conversations, conversationsLoading, conversationsError } = this.state;
    return (
      <section className="conversation-panel recent-conversations-panel conversion-no-print">
        <div className="conversation-panel-header">
          <h2>Recent Conversations</h2>
          <Link to="/conversions/conversations">View all</Link>
        </div>
        <div className="conversation-list">
          {conversationsLoading ? <div className="panel-loading"><span className="spinner-border spinner-border-sm" /> Loading…</div> : null}
          {conversationsError ? (
            <div className="panel-inline-error"><span>{conversationsError}</span><button type="button" onClick={this.loadRecentConversations}>Retry</button></div>
          ) : null}
          {!conversationsLoading && !conversationsError && !conversations.length ? <div className="panel-empty">No conversations yet.</div> : null}
          {!conversationsLoading && !conversationsError ? conversations.map((conversation) => (
            <Link className="conversation-row" key={conversation.id} to={`/conversions/conversations?conversation=${conversation.id}`}>
              <span className="conversation-avatar">{getInitials(conversation.name)}</span>
              <span className="conversation-person"><strong>{conversation.name}</strong><small>{conversation.phone}</small></span>
              <span className="conversation-preview">{conversation.preview}</span>
              <span className="conversation-time">{formatShortDate(conversation.time)}</span>
              <span className={`conversation-status ${conversation.status === "awaiting_reply" ? "waiting" : "replied"}`}>
                {conversation.status === "awaiting_reply" ? "Waiting" : "Replied"}
              </span>
            </Link>
          )) : null}
        </div>
      </section>
    );
  }

  renderActiveFlows() {
    const { activeFlows, flowsLoading, flowsError } = this.state;
    return (
      <section className="conversation-panel active-flows-panel conversion-no-print">
        <div className="conversation-panel-header"><h2>My Active Flows</h2><Link to="/conversions/automation-flows">View all flows</Link></div>
        <div className="active-flow-table-wrap">
          {flowsLoading ? <div className="panel-loading"><span className="spinner-border spinner-border-sm" /> Loading…</div> : null}
          {flowsError ? <div className="panel-inline-error"><span>{flowsError}</span><button type="button" onClick={this.loadActiveFlows}>Retry</button></div> : null}
          {!flowsLoading && !flowsError && !activeFlows.length ? <div className="panel-empty">No active flows yet.</div> : null}
          {!flowsLoading && !flowsError && activeFlows.length ? (
            <table className="active-flow-table">
              <thead><tr><th>Flow Name</th><th>Event</th><th>Status</th><th>Replies</th><th>Last Updated</th><th /></tr></thead>
              <tbody>{activeFlows.map((flow) => (
                <tr key={flow.id}>
                  <td>{flow.name}</td><td>{flow.eventName}</td><td><span className="flow-active-chip">Active</span></td>
                  <td>{formatNumber(flow.replies)}</td><td>{flow.updatedAt ? moment(flow.updatedAt).format("MMM D, YYYY") : "—"}</td>
                  <td><Link to={`/conversions/automation-flows/${flow.id}`} aria-label={`View ${flow.name}`}><FiArrowRight /></Link></td>
                </tr>
              ))}</tbody>
            </table>
          ) : null}
        </div>
      </section>
    );
  }

  renderBillingSnapshot() {
    const { selectedMonth, overview, loading, exportingCsv } = this.state;
    const billingMonth = overview.billing_month || {};
    const availableMonths = Array.isArray(overview.available_months) ? overview.available_months : [];
    const totals = overview.overview || {};
    const billing = overview.billing_status_breakdown || {};
    const currency = overview.currency || "AUD";
    const cards = [
      { label: "Total Charged", value: formatMoney(totals.total_charged, currency), icon: <FiFileText /> },
      { label: "Current Outstanding Balance", value: formatMoney(totals.current_outstanding_balance, currency), icon: <FiActivity /> },
      { label: "Unbilled", value: formatMoney(billing.unbilled && billing.unbilled.amount, currency), icon: <FiClock /> },
      { label: "SMS Sent", value: formatNumber(totals.sms_sent), icon: <FiSend /> },
    ];
    return (
      <section className={`conversation-panel billing-snapshot-panel ${loading ? "is-loading" : ""}`}>
        <div className="conversation-panel-header">
          <div><h2>Billing Snapshot</h2><small>{billingMonth.label || selectedMonth}</small></div>
          <Link className="conversion-no-print" to="/conversions/billing">View billing</Link>
        </div>
        <div className="billing-dashboard-controls conversion-no-print">
          <label>
            <span>Billing month</span>
            <select value={selectedMonth} disabled={loading} onChange={this.handleMonthChange} aria-label="Billing month">
              {availableMonths.map((month) => <option key={month.value} value={month.value}>{month.label}</option>)}
            </select>
          </label>
          <button type="button" disabled={loading || !overview} onClick={this.handleExportPdf}><FiPrinter /> PDF</button>
          <button type="button" disabled={loading || exportingCsv || !overview} onClick={this.handleExportCsv}>
            <FiDownload /> {exportingCsv ? "Exporting…" : "CSV"}
          </button>
        </div>
        <div className="billing-snapshot-grid">
          {cards.map((card) => <div className="billing-snapshot-card" key={card.label}><span>{card.label}</span><strong>{card.value}</strong>{card.icon}</div>)}
        </div>
        <p className="billing-rate">Postpaid billing · {formatMoney(overview.price_per_reply, currency)} per successful customer reply</p>
        {loading ? <div className="conversion-loading-overlay"><span className="spinner-border" role="status" aria-label="Loading selected billing month" /></div> : null}
      </section>
    );
  }

  renderQuickTips() {
    const tips = [
      [<FiUsers />, "Keep your replies fast", "Replying within 15 minutes helps keep customers engaged."],
      [<FiMessageCircle />, "Personalise messages", "Use the customer’s first name to build better connections."],
      [<FiActivity />, "Track performance", "Check Analytics regularly to see what’s working."],
      [<FiFileText />, "Use templates", "Templates save time and help drive more replies."],
    ];
    return (
      <aside className="conversation-panel quick-tips-panel conversion-no-print">
        <div className="conversation-panel-header"><h2><MdOutlineTipsAndUpdates /> Quick tips</h2></div>
        <div className="quick-tip-list">{tips.map(([icon, title, text]) => (
          <div className="quick-tip" key={title}><span>{icon}</span><div><strong>{title}</strong><p>{text}</p></div></div>
        ))}</div>
      </aside>
    );
  }

  renderDashboard() {
    const { overview, error } = this.state;
    return (
      <div className="conversations-dashboard">
        <h2 className="conversion-print-heading">Conversations Billing — {overview.billing_month && overview.billing_month.label}</h2>
        {error ? <div className="conversations-page-alert conversion-no-print" role="alert">{error}</div> : null}
        {this.renderHero()}
        {this.renderTabs()}
        {this.renderMetricCards()}
        <div className="conversations-dashboard-grid">
          {this.renderGettingStarted()}
          {this.renderRecentConversations()}
          {this.renderActiveFlows()}
          {this.renderBillingSnapshot()}
          {this.renderQuickTips()}
        </div>
      </div>
    );
  }

  render() {
    const { overview } = this.state;
    return (
      <SmsAccessGuard>
        <Layout>
          <main className="page conversion-billing-page">
            {overview ? this.renderDashboard() : this.renderInitialState()}
          </main>
        </Layout>
      </SmsAccessGuard>
    );
  }
}

export default SmsBilling;
